/**
 * Normaliza texto para comparaciones de búsqueda: minúsculas y sin tildes
 * ni diacríticos ("Tomás" → "tomas").
 */
export function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/**
 * True si `texto` coincide con `busqueda`, ignorando mayúsculas y tildes.
 * Usar en cualquier filtro de texto libre del cliente.
 *
 * Acepta tres formas de escribir, de la más literal a la más suelta:
 * para "Valezca Escarez" coinciden "escarez" (contiene), "ve" (iniciales)
 * y "es va" (partes de cada palabra, en cualquier orden).
 */
export function matchesSearch(texto: string | null | undefined, busqueda: string): boolean {
  if (!texto) return false;

  const objetivo = normalizeText(texto);
  const consulta = normalizeText(busqueda).trim();
  if (!consulta) return true;

  // 1. Lo escrito aparece tal cual, seguido.
  if (objetivo.includes(consulta)) return true;

  const palabras = objetivo.split(/\s+/).filter(Boolean);
  const terminos = consulta.split(/\s+/).filter(Boolean);

  // 2. Iniciales de las palabras: "ve" → "Valezca Escarez".
  //    Desde dos letras: con una sola, el paso 1 ya cubre lo razonable
  //    y esta regla solo agregaría ruido.
  if (terminos.length === 1 && consulta.length >= 2) {
    const iniciales = palabras.map(palabra => palabra[0]).join('');
    if (iniciales.includes(consulta)) return true;
  }

  // 3. Cada término inicia una palabra distinta, sin importar el orden:
  //    "es va" → "Valezca Escarez".
  if (terminos.length > 1) {
    // deuda: emparejamiento greedy — el primer término que calza consume la palabra,
    // sin backtracking. techo: nombres y descripciones de pocas palabras, donde los
    // términos rara vez compiten por la misma. disparador: si aparece un caso real
    // donde un match existe pero no se encuentra (ej. "ma mar" contra "Mar Marina"),
    // reemplazar por emparejamiento bipartito.
    const disponibles = [...palabras];
    return terminos.every(termino => {
      const indice = disponibles.findIndex(palabra => palabra.startsWith(termino));
      if (indice === -1) return false;
      disponibles.splice(indice, 1);
      return true;
    });
  }

  return false;
}
